import React, { useEffect, useState, useMemo } from "react";
import { X, Search, BookUser, Mail, Phone, Folder, ExternalLink, Pencil, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Modal Rubrica Agenti (admin-only).
 *
 * Lista tutti gli agenti già inseriti in qualsiasi fornitore, con:
 *  - nome, email, telefono
 *  - lista dei fornitori dove l'agente è configurato (cliccabili per saltare al pannello)
 * Ricerca live su nome / email / fornitore.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(supplierName: string) => void} [props.onJumpSupplier] - opzionale, callback quando l'admin
 *   clicca un fornitore nella rubrica (per aprire il drawer del fornitore corrispondente).
 */
export default function AgentDirectoryModal({ open, onClose, onJumpSupplier }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  // editing: { idx, originalName, originalEmail, name, email, phone, saving }
  const [editing, setEditing] = useState(null);

  function reload() {
    setLoading(true);
    return api.get("/admin/supplier-agents")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!open) return;
    reload();
  }, [open]);

  function startEdit(idx, a) {
    setEditing({ idx, originalName: a.name, originalEmail: a.email, name: a.name, email: a.email, phone: a.phone, saving: false });
  }

  async function saveEdit() {
    if (!editing) return;
    setEditing({ ...editing, saving: true });
    try {
      const { data } = await api.put("/admin/supplier-agents", {
        old_name: editing.originalName,
        old_email: editing.originalEmail,
        new_name: editing.name,
        new_email: editing.email,
        new_phone: editing.phone,
      });
      toast.success(`Aggiornato su ${data.updated} fornitor${data.updated === 1 ? "e" : "i"}`);
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
      setEditing({ ...editing, saving: false });
    }
  }

  /**
   * Cancella l'agente svuotando i campi `agent_name/email/phone` su tutti i fornitori
   * dove appare. Richiede doppia conferma esplicita (typing del nome) per evitare
   * cancellazioni accidentali.
   */
  async function deleteAgent(a) {
    const supplierCount = a.supplier_names?.length || 0;
    const confirmLabel = a.name || a.email;
    // Conferma con prompt
    const typed = window.prompt(
      `Per eliminare definitivamente l'agente "${confirmLabel}" da ${supplierCount} fornitor${supplierCount === 1 ? "e" : "i"}, scrivi qui sotto il nome esatto.\n\n(I campi agent_name/email/telefono verranno svuotati sui fornitori coinvolti; potrai poi assegnare l'agente corretto)`
    );
    if (typed == null) return; // cancel
    if (typed.trim().toLowerCase() !== (a.name || "").trim().toLowerCase()) {
      toast.error("Conferma non corrispondente · operazione annullata.");
      return;
    }
    try {
      const { data } = await api.delete("/admin/supplier-agents", {
        data: { name: a.name, email: a.email },
      });
      toast.success(`Eliminato da ${data.removed} fornitor${data.removed === 1 ? "e" : "i"}`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    }
  }

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((a) =>
      a.name.toLowerCase().includes(s) ||
      (a.email || "").toLowerCase().includes(s) ||
      (a.phone || "").toLowerCase().includes(s) ||
      (a.supplier_names || []).some((n) => n.toLowerCase().includes(s))
    );
  }, [q, items]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="agent-directory-modal">
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-cream w-full max-w-2xl max-h-[85vh] flex flex-col rounded-sm shadow-2xl border border-ink/10 mx-4">
        <div className="flex items-center justify-between p-5 border-b border-ink/10">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate1 inline-flex items-center gap-1.5">
              <BookUser size={11} /> Solo admin · gestione
            </div>
            <div className="font-display text-xl text-ink mt-1">Rubrica Agenti</div>
            <div className="text-[11px] text-slate1 mt-0.5">{items.length} agenti distinti · click su un fornitore per aprirlo</div>
          </div>
          <button
            onClick={onClose}
            className="text-slate1 hover:text-ink"
            data-testid="agent-directory-close"
            aria-label="Chiudi"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 border-b border-ink/10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" size={14} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per nome, email, telefono o fornitore..."
              className="w-full pl-9 pr-3 py-2.5 border border-ink/20 bg-white rounded-sm text-sm focus:outline-none focus:border-ink"
              data-testid="agent-directory-search"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-3">
          {loading && <div className="text-slate1 text-sm">Caricamento…</div>}
          {!loading && filtered.length === 0 && items.length === 0 && (
            <div className="text-slate1 text-sm">
              Nessun agente in rubrica. Compila il campo &ldquo;Agente&rdquo; su almeno un fornitore: comparirà qui automaticamente e potrai riutilizzarlo.
            </div>
          )}
          {!loading && filtered.length === 0 && items.length > 0 && (
            <div className="text-slate1 text-sm">Nessun risultato per &ldquo;{q}&rdquo;.</div>
          )}
          {filtered.map((a, idx) => {
            const isEditing = editing && editing.originalName === a.name && editing.originalEmail === a.email;
            if (isEditing) {
              return (
                <div
                  key={`${a.name}-${a.email}-${idx}`}
                  className="bg-amber-50 border-2 border-amber-300 rounded-sm p-4"
                  data-testid={`agent-row-${idx}-editing`}
                >
                  <div className="text-[10px] tracking-[0.2em] uppercase text-amber-800 mb-2">
                    Modifica · le modifiche verranno propagate ai {a.supplier_names.length} fornitor{a.supplier_names.length === 1 ? "e" : "i"}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <label className="text-[9px] uppercase tracking-[0.15em] text-slate1">Nome</label>
                      <input
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        className="mt-0.5 w-full px-2 py-1.5 border border-amber-300 bg-white text-sm rounded-sm focus:outline-none focus:border-ink"
                        data-testid={`agent-edit-name-${idx}`}
                      />
                    </div>
                    <div>
                      <label className="text-[9px] uppercase tracking-[0.15em] text-slate1">Email</label>
                      <input
                        type="email"
                        value={editing.email}
                        onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                        className="mt-0.5 w-full px-2 py-1.5 border border-amber-300 bg-white text-sm rounded-sm focus:outline-none focus:border-ink"
                        data-testid={`agent-edit-email-${idx}`}
                      />
                    </div>
                    <div>
                      <label className="text-[9px] uppercase tracking-[0.15em] text-slate1">Telefono</label>
                      <input
                        value={editing.phone}
                        onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                        className="mt-0.5 w-full px-2 py-1.5 border border-amber-300 bg-white text-sm rounded-sm focus:outline-none focus:border-ink"
                        data-testid={`agent-edit-phone-${idx}`}
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setEditing(null)}
                      className="px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase text-slate1 border border-ink/20 rounded-sm hover:bg-cream2"
                      data-testid={`agent-edit-cancel-${idx}`}
                      disabled={editing.saving}
                    >
                      Annulla
                    </button>
                    <button
                      type="button"
                      onClick={saveEdit}
                      disabled={editing.saving}
                      className="px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase bg-ink text-cream rounded-sm hover-lift inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid={`agent-edit-save-${idx}`}
                    >
                      <Save size={11} />
                      {editing.saving ? "Salvataggio…" : `Salva e propaga (${a.supplier_names.length})`}
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <div
                key={`${a.name}-${a.email}-${idx}`}
                className="bg-white border border-ink/10 rounded-sm p-4"
                data-testid={`agent-row-${idx}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-display text-base text-ink">{a.name || "(senza nome)"}</div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      type="button"
                      onClick={() => startEdit(idx, a)}
                      className="text-slate1 hover:text-ink inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase"
                      data-testid={`agent-row-${idx}-edit`}
                      title="Modifica e propaga su tutti i fornitori"
                    >
                      <Pencil size={11} /> Modifica
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteAgent(a)}
                      className="text-rose-600 hover:text-rose-800 inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase"
                      data-testid={`agent-row-${idx}-delete`}
                      title="Elimina agente da tutti i fornitori"
                    >
                      <Trash2 size={11} /> Elimina
                    </button>
                  </div>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  {a.email && (
                    <a
                      href={`mailto:${a.email}`}
                      className="text-slate1 hover:text-ink inline-flex items-center gap-1"
                      data-testid={`agent-row-${idx}-email`}
                    >
                      <Mail size={11} /> {a.email}
                    </a>
                  )}
                  {a.phone && (
                    <a
                      href={`tel:${a.phone.replace(/\s/g, "")}`}
                      className="text-slate1 hover:text-ink inline-flex items-center gap-1"
                      data-testid={`agent-row-${idx}-phone`}
                    >
                      <Phone size={11} /> {a.phone}
                    </a>
                  )}
                </div>
                {a.supplier_names && a.supplier_names.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <span className="text-[9px] tracking-[0.18em] uppercase text-slate1 self-center mr-1 inline-flex items-center gap-1">
                      <Folder size={10} /> Fornitori
                    </span>
                    {a.supplier_names.map((sn) => (
                      <button
                        type="button"
                        key={sn}
                        onClick={() => { if (onJumpSupplier) onJumpSupplier(sn); onClose(); }}
                        className="px-2 py-0.5 bg-cream2 hover:bg-ink hover:text-cream border border-ink/10 rounded-sm text-[11px] inline-flex items-center gap-1 transition-colors"
                        data-testid={`agent-row-${idx}-supplier`}
                        title="Apri scheda fornitore"
                      >
                        {sn} <ExternalLink size={9} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
